import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, AlertCircle, Clock, Building2, Search, Loader2, ClipboardList, PlayCircle, Users, HandMetal, Umbrella, FileDown } from "lucide-react";
import { toast } from "sonner";

const STATO_PILL = {
  da_fare: { label: "Da fare", cls: "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200" },
  completato: { label: "Completato", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300" },
  non_eseguito: { label: "Non eseguito", cls: "bg-rose-100 text-rose-700 dark:bg-rose-900/50 dark:text-rose-300" },
  in_ritardo: { label: "In ritardo", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300" },
};

const VAL_PILL = {
  perfetto: "bg-emerald-200 text-emerald-900 dark:bg-emerald-800/60 dark:text-emerald-200",
  buono: "bg-lime-100 text-lime-800 dark:bg-lime-900/40 dark:text-lime-300",
  sufficiente: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  insufficiente: "bg-rose-200 text-rose-900 dark:bg-rose-800/60 dark:text-rose-200",
};

const VAL_LABEL = { perfetto: "Perfetto", buono: "Buono", sufficiente: "Sufficiente", insufficiente: "Insufficiente" };

export default function Controlli() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [hotels, setHotels] = useState([]);
  // Filtri controllati via URL params (bookmark/back-friendly)
  const tab = searchParams.get("tab") || "oggi";
  const q = searchParams.get("q") || "";
  const hotelFilter = searchParams.get("hotel_id") || "all";
  const statoFilter = searchParams.get("stato") || "all";
  const setParam = (key, value, defaultValue) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === defaultValue) next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  };
  const setTab = (v) => setParam("tab", v, "oggi");
  const setQ = (v) => setParam("q", v, "");
  const setHotelFilter = (v) => setParam("hotel_id", v, "all");
  const setStatoFilter = (v) => setParam("stato", v, "all");

  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [absenceSummary, setAbsenceSummary] = useState(null);

  useEffect(() => {
    api.get("/hotels").then((r) => setHotels(r.data || []));
    // Carico riepilogo assenze recenti (banner Bug 2)
    api.get("/checks/absence-summary", { params: { days: 30 } })
      .then((r) => setAbsenceSummary(r.data || null))
      .catch(() => setAbsenceSummary(null));
  }, []);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const past = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);
      if (tab === "oggi") {
        const { data } = await api.get("/checks/today", { headers: { "Cache-Control": "no-store" } });
        setChecks(data || []);
      } else if (tab === "settimana") {
        const { data } = await api.get("/checks", { params: { data_da: past, data_a: today } });
        setChecks(data || []);
      } else {
        const { data } = await api.get("/checks", { params: { data_a: past } });
        setChecks(data || []);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [tab]);

  // Polling ogni 15s solo sulla vista "oggi" (task condivise fra manager)
  const pollRef = useRef(null);
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (tab !== "oggi") return;
    pollRef.current = setInterval(() => {
      if (document.visibilityState === "visible") load(true);
    }, 15000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const doClaim = async (checkId, e) => {
    e.stopPropagation();
    try {
      await api.post(`/checks/${checkId}/claim`);
      toast.success("Presa in carico");
      load(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.claimer) {
        toast.warning(`Già in carico a ${detail.claimer.nome} ${detail.claimer.cognome}`);
      } else {
        toast.error("Errore claim");
      }
    }
  };

  const filtered = useMemo(() => {
    return checks.filter((c) => {
      if (hotelFilter !== "all" && c.hotel_id !== hotelFilter) return false;
      if (statoFilter !== "all" && c.stato !== statoFilter) return false;
      if (q && !c.titolo.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    }).sort((a, b) => {
      // sort by hotel then slot_ora (null last)
      if (a.hotel_id !== b.hotel_id) return (a.hotel_id || "").localeCompare(b.hotel_id || "");
      const ao = a.slot_ora || "99:99";
      const bo = b.slot_ora || "99:99";
      return ao.localeCompare(bo);
    });
  }, [checks, q, hotelFilter, statoFilter]);

  const hotelName = (id) => hotels.find((h) => h.id === id)?.nome || "—";

  const stats = useMemo(() => {
    const total = checks.length;
    const done = checks.filter((c) => c.stato === "completato").length;
    const todo = checks.filter((c) => c.stato === "da_fare").length;
    const missed = checks.filter((c) => c.stato === "non_eseguito").length;
    return { total, done, todo, missed };
  }, [checks]);

  return (
    <div className="space-y-6 ih-anim-in" data-testid="controlli-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2 flex items-center gap-2">
            <ClipboardList className="h-3 w-3" /> Controlli operativi
          </div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-light">
            Controlli <span className="font-medium">giornalieri</span>
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">{loading ? "Caricamento…" : `${filtered.length} di ${stats.total} visibili`}</p>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard label="Totale" value={stats.total} />
        <KpiCard label="Da fare" value={stats.todo} tone="muted" icon={<Clock className="h-4 w-4" />} />
        <KpiCard label="Completati" value={stats.done} tone="emerald" icon={<CheckCircle2 className="h-4 w-4" />} />
        <KpiCard label="Non eseguiti" value={stats.missed} tone="rose" icon={<AlertCircle className="h-4 w-4" />} />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="controlli-tabs">
          <TabsTrigger value="oggi" data-testid="tab-oggi">Oggi</TabsTrigger>
          <TabsTrigger value="settimana" data-testid="tab-settimana">Ultima settimana</TabsTrigger>
          <TabsTrigger value="storico" data-testid="tab-storico">Storico</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* v103 — Download PDF Recap Giornaliero (sulla data del tab oggi, hotel filtrato) */}
      <div className="flex justify-end -mt-2">
        <button
          type="button"
          onClick={async () => {
            try {
              const d = new Date().toISOString().slice(0, 10);
              const params = new URLSearchParams({ date: d });
              if (hotelFilter !== "all") params.set("hotel_id", hotelFilter);
              const r = await api.get(`/checks/daily-recap-pdf?${params}`, { responseType: "blob" });
              const url = URL.createObjectURL(r.data);
              const a = document.createElement("a");
              a.href = url;
              a.download = `IH-RecapGiornaliero-${d}.pdf`;
              document.body.appendChild(a); a.click(); a.remove();
              setTimeout(() => URL.revokeObjectURL(url), 2000);
            } catch (e) {
              toast.error("Impossibile generare il PDF giornaliero");
            }
          }}
          data-testid="daily-recap-pdf-btn"
          title="Scarica PDF riepilogo di tutti i controlli di oggi"
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-semibold border border-lavender-300/70 bg-lavender-50/50 dark:bg-lavender-900/20 text-lavender-700 dark:text-lavender-300 hover:bg-lavender-100 dark:hover:bg-lavender-900/40 transition-all active:scale-[0.97]"
        >
          <FileDown className="h-3.5 w-3.5" />
          Scarica PDF Giornata
        </button>
      </div>

      {/* Banner assenze recenti — visibile solo su Storico/Settimana */}
      {tab !== "oggi" && absenceSummary?.periods?.length > 0 && (
        <div
          data-testid="absence-summary-banner"
          className="ih-glass rounded-2xl p-4 border-l-4 border-l-lavender-400 flex items-start gap-3"
        >
          <div className="h-9 w-9 rounded-full bg-lavender-100 dark:bg-lavender-900/40 flex items-center justify-center shrink-0">
            <Umbrella className="h-4 w-4 text-lavender-700 dark:text-lavender-300" />
          </div>
          <div className="flex-1 text-sm">
            <div className="font-medium mb-0.5">
              {absenceSummary.periods.length === 1
                ? "Sei stato assente in un periodo recente"
                : `Sei stato assente in ${absenceSummary.periods.length} periodi recenti`}
            </div>
            <ul className="space-y-0.5 text-xs text-muted-foreground">
              {absenceSummary.periods.slice(0, 4).map((p, i) => (
                <li key={i} data-testid={`absence-period-${i}`}>
                  <span className="capitalize font-medium text-foreground/80">{p.tipo}</span>{" "}
                  dal {new Date(p.from).toLocaleDateString("it-IT", { day: "numeric", month: "short" })}
                  {" "}al {new Date(p.to).toLocaleDateString("it-IT", { day: "numeric", month: "short" })}
                  {" — "}
                  <span className="text-foreground/70">
                    {p.handled_by_colleagues} task{p.handled_by_colleagues === 1 ? "" : ""} gestite dai colleghi
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="ih-glass rounded-2xl p-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="controlli-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per titolo…" className="pl-9 h-10 bg-background/60" />
        </div>
        {hotels.length > 1 && (
          <Select value={hotelFilter} onValueChange={setHotelFilter}>
            <SelectTrigger className="w-full sm:w-[220px] h-10" data-testid="filter-hotel">
              <SelectValue placeholder="Hotel" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli hotel</SelectItem>
              {hotels.map((h) => <SelectItem key={h.id} value={h.id}>{h.nome}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={statoFilter} onValueChange={setStatoFilter}>
          <SelectTrigger className="w-full sm:w-[180px] h-10" data-testid="filter-stato">
            <SelectValue placeholder="Stato" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="da_fare">Da fare</SelectItem>
            <SelectItem value="in_ritardo">In ritardo</SelectItem>
            <SelectItem value="completato">Completato</SelectItem>
            <SelectItem value="non_eseguito">Non eseguito</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => <div key={i} className="ih-glass rounded-2xl h-40 animate-pulse" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="ih-glass rounded-2xl p-12 text-center">
          <ClipboardList className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
          <p className="font-medium">Nessun controllo</p>
          <p className="text-sm text-muted-foreground">Modifica i filtri o aspetta lo scheduler.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((c) => {
            const pill = STATO_PILL[c.stato] || STATO_PILL.da_fare;
            const isMine = c.assigned_to_user_id === user?.id;
            const isShared = c.is_shared === true;
            const otherAssignee = !isMine && c.assigned_to_user_id;
            const claimedByOther = c.claimed_by_user_id && c.claimed_by_user_id !== user?.id;
            const completedByOther = c.stato === "completato" && c.completer_user_id && c.completer_user_id !== user?.id;
            const returnTo = `${location.pathname}${location.search}`;
            return (
              <div key={c.id} data-testid={`check-card-${c.id}`} className="ih-glass rounded-2xl p-5 ih-card-hover cursor-pointer" onClick={() => navigate(`/controlli/${c.id}`, { state: { returnTo } })}>
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{c.categoria.replaceAll("_", " ")}</div>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${pill.cls}`}>{pill.label}</span>
                </div>
                <h3 className="font-display text-lg font-semibold leading-tight mb-2 flex items-center gap-2 flex-wrap">
                  <span>{c.titolo}</span>
                  {c.slot_ora && (
                    <span
                      data-testid={`slot-badge-${c.id}`}
                      className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
                        c.slot_id === "mattina"
                          ? "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300"
                          : c.slot_id === "pomeriggio"
                          ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
                          : "bg-accent/60 text-foreground/80"
                      }`}
                    >
                      {c.slot_label || c.slot_id} · {c.slot_ora}
                    </span>
                  )}
                </h3>
                <div className="text-xs text-muted-foreground flex items-center gap-1.5 mb-2"><Building2 className="h-3 w-3" /> {hotelName(c.hotel_id)}</div>
                {/* Badge task condivisa / claim / completer — solo su hotel multi-manager */}
                <div className="flex items-center gap-1.5 flex-wrap mb-2 min-h-[20px]">
                  {completedByOther && (
                    <span data-testid={`completed-by-${c.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                      <CheckCircle2 className="h-3 w-3" /> Completata da {c.completer_name}
                    </span>
                  )}
                  {!completedByOther && isShared && claimedByOther && (
                    <span data-testid={`claimed-by-${c.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                      <HandMetal className="h-3 w-3" /> In carico: {c.claimer_name}
                    </span>
                  )}
                  {!completedByOther && !claimedByOther && isShared && otherAssignee && (
                    <span data-testid={`shared-badge-${c.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-lavender-100 text-lavender-700 dark:bg-lavender-900/40 dark:text-lavender-300">
                      <Users className="h-3 w-3" /> Condivisa · assegnata a {c.assignee_name || "collega"}
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Scadenza: {c.scadenza ? new Date(c.scadenza).toLocaleString("it-IT", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}</span>
                  {c.valutazione && <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${VAL_PILL[c.valutazione]}`}>{VAL_LABEL[c.valutazione]}</span>}
                </div>
                {(c.stato === "da_fare" || c.stato === "in_ritardo") && (
                  <div className="flex gap-2 mt-4">
                    {isShared && (otherAssignee || claimedByOther) && !isMine && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => doClaim(c.id, e)}
                        data-testid={`check-claim-${c.id}`}
                        className="gap-1.5"
                      >
                        <HandMetal className="h-3.5 w-3.5" /> Prendo io
                      </Button>
                    )}
                    <Button
                      size="sm"
                      className={`flex-1 gap-1.5 ${c.stato === "in_ritardo" ? "bg-amber-500 hover:bg-amber-600 text-white" : ""}`}
                      data-testid={`check-execute-${c.id}`}
                    >
                      <PlayCircle className="h-3.5 w-3.5" />
                      {c.stato === "in_ritardo" ? "Esegui in ritardo" : "Esegui controllo"}
                    </Button>
                  </div>
                )}
                {c.stato === "completato" && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-4 w-full gap-1.5"
                    data-testid={`check-view-${c.id}`}
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" /> Vedi risultato
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, tone = "lavender", icon }) {
  return (
    <div className="ih-glass rounded-2xl p-4">
      <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground flex items-center gap-1.5">{icon} {label}</div>
      <div className="font-display text-3xl tracking-tight font-medium mt-1">{value}</div>
    </div>
  );
}
